import { z } from "zod";
import { defineEndpoint } from "./endpoint.js";
import { storefrontHostSchema } from "./storefront.js";
import { SHOP_GATEWAYS } from "./storefront-checkout.js";
/**
 * Online payments of the online store (B-77, SF-09): each shop connects its own bKash, Nagad or SSLCommerz merchant
 * account (owner decision 2026-10-30: money goes straight to the merchant). A payment counts only after the gateway's
 * server-side validation, once per gateway transaction ID.
 */
const tag = { tag: "storefront" };
const gatewaySchema = z.enum(SHOP_GATEWAYS);
/** What each gateway needs (write-only: the API never returns them). */
export const gatewayCredentialsSchema = z.discriminatedUnion("gateway", [
    z.object({
        gateway: z.literal("sslcommerz"),
        storeId: z.string().trim().min(1).max(100),
        storePassword: z.string().trim().min(1).max(200),
    }),
    z.object({
        gateway: z.literal("bkash"),
        appKey: z.string().trim().min(1).max(200),
        appSecret: z.string().trim().min(1).max(200),
        username: z.string().trim().min(1).max(100),
        password: z.string().trim().min(1).max(200),
    }),
    z.object({
        gateway: z.literal("nagad"),
        merchantId: z.string().trim().min(1).max(50),
        /** The merchant's RSA private key (PEM or base64 PKCS#8). */
        merchantPrivateKey: z.string().trim().min(100).max(5000),
        /** Nagad's RSA public key (PEM or base64 SPKI). */
        nagadPublicKey: z.string().trim().min(100).max(5000),
    }),
]);
export const shopGatewaySchema = z.object({
    gateway: gatewaySchema,
    enabled: z.boolean(),
    /** The gateway's test environment. */
    sandbox: z.boolean(),
    /** Credentials are stored (never shown). */
    configured: z.boolean(),
    /** The payment account the money is recorded in. */
    accountId: z.uuid().nullable(),
    version: z.int(),
});
export const listShopGateways = defineEndpoint({
    ...tag,
    name: "listShopGateways",
    method: "GET",
    path: "/v1/storefront-gateways",
    access: { permission: "storefront.view" },
    summary: "The online store's payment gateways (credentials are never shown)",
    status: 200,
    response: z.object({ data: z.array(shopGatewaySchema) }),
});
export const updateShopGatewaySchema = z
    .object({
    enabled: z.boolean(),
    sandbox: z.boolean(),
    accountId: z.uuid().nullable(),
    /** Left out: keep the stored credentials. */
    credentials: gatewayCredentialsSchema.optional(),
    version: z.int().min(0),
})
    .strict();
export const updateShopGateway = defineEndpoint({
    ...tag,
    name: "updateShopGateway",
    method: "PUT",
    path: "/v1/storefront-gateways/{gateway}",
    access: { permission: "storefront.manage" },
    summary: "Connect or change a payment gateway (credentials are encrypted at rest)",
    params: z.object({ gateway: gatewaySchema }),
    body: updateShopGatewaySchema,
    status: 200,
    response: shopGatewaySchema,
    errors: ["CONFLICT", "BUSINESS_RULE_VIOLATION"],
});
// Public: the shopper comes back from the gateway ------------------------------------------------------------------
export const PAYMENT_SESSION_STATUSES = ["open", "paid", "failed", "expired"];
export const shopPaymentResultSchema = z.object({
    status: z.enum(PAYMENT_SESSION_STATUSES),
    orderNumber: z.string(),
    totalPaisa: z.int(),
    /** Open again: where to try paying once more. */
    retryUrl: z.url().nullable(),
    /** DG-09: every line is digital — nothing is shipped; the products are in the shopper's library once paid. */
    digitalOnly: z.boolean().default(false),
});
export const confirmShopPayment = defineEndpoint({
    ...tag,
    name: "confirmShopPayment",
    method: "POST",
    path: "/v1/storefront/payments/{sessionId}/confirm",
    access: { public: true },
    summary: "After the gateway sent the shopper back: validate the payment with the gateway (server to server)",
    params: z.object({ sessionId: z.uuid() }),
    body: z
        .object({
        host: storefrontHostSchema,
        /** What the gateway put on the return URL (its IDs); checked against the gateway, never trusted. */
        params: z.record(z.string().max(60), z.string().max(500)),
    })
        .strict(),
    status: 200,
    response: shopPaymentResultSchema,
});
//# sourceMappingURL=storefront-payments.js.map